import type { TemplateField } from '@branditdev/mf-types'

type TemplateOptionValue = Record<string, boolean | string | number>
type TemplateOptionValueMap = Record<string, TemplateOptionValue>
type StoredTemplateFieldValue = Partial<TemplateField> & {
  value?: TemplateField['value']
}

export type TemplateFieldValuesState = Record<
  string | number,
  StoredTemplateFieldValue | Record<string | number, StoredTemplateFieldValue> | undefined
>

type BundleProductTemplateSource = {
  bundle_group_product_id: string | number
  has_template?: boolean
  template?: TemplateField[] | { template_fields?: TemplateField[] } | null
}

type TemplateFieldValueScope = {
  isBundle?: boolean
  bundleProduct?: Pick<BundleProductTemplateSource, 'bundle_group_product_id'>
}

const optionInputTypes: Array<TemplateField['input_type']> = ['radio', 'multiselect', 'checkbox']

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

export const getTemplateFieldInputName = (
  field: Pick<TemplateField, 'template_field_id'>,
  scope: TemplateFieldValueScope = {}
) => {
  return scope.isBundle && scope.bundleProduct
    ? `input_${scope.bundleProduct.bundle_group_product_id}_${field.template_field_id}`
    : `input_${field.template_field_id}`
}

const getBundleProductTemplateFields = (bundleProduct?: BundleProductTemplateSource | null): TemplateField[] => {
  if (!bundleProduct?.template) return []

  const fields = Array.isArray(bundleProduct.template)
    ? bundleProduct.template
    : (bundleProduct.template.template_fields ?? [])

  return fields.filter(field => field.template_id)
}

const getBundleTemplateValues = (
  templateFieldValues: TemplateFieldValuesState = {},
  bundleProduct?: Pick<BundleProductTemplateSource, 'bundle_group_product_id'> | null
): Record<string | number, StoredTemplateFieldValue> => {
  if (!bundleProduct) return {}

  const storedValues = templateFieldValues[bundleProduct.bundle_group_product_id]

  if (!isRecord(storedValues) || 'value' in storedValues) return {}

  return storedValues as Record<string | number, StoredTemplateFieldValue>
}

const getTemplateFieldValueById = (
  templateFieldValues: TemplateFieldValuesState = {},
  fieldId: string | number,
  scope: TemplateFieldValueScope = {}
): StoredTemplateFieldValue | undefined => {
  if (scope.isBundle) {
    return getBundleTemplateValues(templateFieldValues, scope.bundleProduct)[fieldId]
  }

  const storedValue = templateFieldValues[fieldId]

  return isRecord(storedValue) ? (storedValue as StoredTemplateFieldValue) : undefined
}

export const getTemplateFieldValue = (
  templateFieldValues: TemplateFieldValuesState = {},
  field: Pick<TemplateField, 'template_field_id'>,
  scope: TemplateFieldValueScope = {}
) => getTemplateFieldValueById(templateFieldValues, field.template_field_id, scope)?.value

export const getScalarFieldValue = (value: TemplateField['value'] | undefined) =>
  typeof value === 'string' || typeof value === 'number' ? value : ''

export const getOptionValueMap = (value: TemplateField['value'] | undefined): TemplateOptionValueMap =>
  isRecord(value) ? (value as TemplateOptionValueMap) : {}

const optionValueIsSelected = (value: TemplateField['value'] | undefined) => {
  if (!isRecord(value)) return false

  const optionValues = value as Record<string, unknown>

  return Object.keys(optionValues).some(optionKey => {
    const optionValue = optionValues[optionKey]

    return optionValue === true || (isRecord(optionValue) && optionValue[optionKey] === true)
  })
}

export const hasTemplateFieldValue = (value: TemplateField['value'] | undefined) => {
  if (value === undefined || value === null) return false

  if (typeof value === 'string') return value.length > 0 && value !== '-1'

  if (typeof value === 'number') return value > 0

  return optionValueIsSelected(value)
}

export const isTemplateFieldValueValid = (field: Pick<TemplateField, 'input_type' | 'is_required' | 'value'>) => {
  if (!field.is_required) return true

  if (optionInputTypes.includes(field.input_type)) {
    return optionValueIsSelected(field.value)
  }

  return hasTemplateFieldValue(field.value)
}

export const areTemplateFieldValuesPresent = (
  templateFields: TemplateField[] = [],
  templateFieldValues: Record<string | number, StoredTemplateFieldValue> = {}
) =>
  templateFields.every(field => {
    const storedValue = templateFieldValues[field.template_field_id]

    return isTemplateFieldValueValid({
      ...field,
      value: storedValue && storedValue.value !== undefined ? storedValue.value : field.value
    })
  })

export const areProductTemplateFieldValuesPresent = (
  product: { template?: { template_fields?: TemplateField[] } | null },
  templateFieldValues: TemplateFieldValuesState = {}
) =>
  areTemplateFieldValuesPresent(
    product.template?.template_fields ?? [],
    templateFieldValues as Record<string | number, StoredTemplateFieldValue>
  )

export const areBundleTemplateFieldValuesPresent = (
  bundleProducts: BundleProductTemplateSource[] = [],
  templateFieldValues: TemplateFieldValuesState = {}
) =>
  bundleProducts
    .filter(bundleProduct => bundleProduct.has_template === true)
    .every(bundleProduct =>
      areTemplateFieldValuesPresent(
        getBundleProductTemplateFields(bundleProduct),
        getBundleTemplateValues(templateFieldValues, bundleProduct)
      )
    )

export const areTemplateFieldDependenciesMet = ({
  field,
  templateFieldValues,
  isBundle,
  bundleProduct
}: {
  field: TemplateField
  templateFieldValues: TemplateFieldValuesState
  isBundle?: boolean
  bundleProduct?: Pick<BundleProductTemplateSource, 'bundle_group_product_id'>
}) => {
  if (!field.dependency || field.dependency === 'NO') return true

  const values = (field.dependency_field_ids ?? []).map(
    fieldId => getTemplateFieldValueById(templateFieldValues, fieldId, { isBundle, bundleProduct })?.value
  )

  if (field.dependency === 'AND') {
    return values.every(hasTemplateFieldValue)
  }

  if (field.dependency === 'OR') {
    return values.some(hasTemplateFieldValue)
  }

  return false
}
